import Select from '@quintype/em/components/select';
import { TextField } from '@quintype/em/components/text-field';
import { TextArea } from '@quintype/em/components/text-area';
import { Callout } from '@quintype/em/components/callout';
import get from 'lodash/get';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { addShortsConfig, updateHaveUnsavedChanges } from '../../actions/builder-action';
import { checkValidEncodedCustomCode, encodeCustomCode } from '../../utils/utils';
import { ToggleButton } from '../toggle-button';
import styles from '../manage/manage.module.css';

const FLOATING_BUTTON_POSITION_OPTIONS = [
  { label: 'Bottom Left', value: 'bottom-left' },
  { label: 'Bottom Right', value: 'bottom-right' }
];

const ShortsSettings = () => {
  const dispatch = useDispatch();

  const {
    showButtonInStorySummary = false,
    showFloatingButton = false,
    storySummaryButtonText = 'Switch to Shorts Mode',
    readFullStoryButton = 'Read Full Story',
    floatingButtonText = 'Shorts',
    floatingButtonPosition = 'bottom-left',
    allCaughtUpText = 'You are all caught up',
    allStoriesReadText = "You've read all the stories in Sports Cricket.  We'll notify you when new ones arrive.",
    timeSpentText = 'Time Spent',
    storiesReadText = 'Stories Read',
    widgetCode = '',
    targetingId = '',
    mobilePlaceholderHeight = '',
    desktopPlaceholderHeight = ''
  } = useSelector((state: any) => get(state, ['builder', 'config', 'shorts'], {}));

  const update = (key: string, value: string | boolean) => {
    dispatch(addShortsConfig({ [key]: value }));
    dispatch(updateHaveUnsavedChanges(true));
  };

  const selectedPositionOption =
    FLOATING_BUTTON_POSITION_OPTIONS.find(opt => opt.value === floatingButtonPosition) ||
    FLOATING_BUTTON_POSITION_OPTIONS[0];

  return (
    <div className={styles.toggleWrapper}>
      <ToggleButton
        label="Show Button in Story Summary"
        toggleName="showButtonInStorySummary"
        id="showButtonInStorySummary"
        updateToggleState={showButtonInStorySummary}
        updateToggleButton={(_name: string, value: boolean) => update('showButtonInStorySummary', value)}
      />
      <ToggleButton
        label="Show Floating Button"
        toggleName="showFloatingButton"
        id="showFloatingButton"
        updateToggleState={showFloatingButton}
        updateToggleButton={(_name: string, value: boolean) => update('showFloatingButton', value)}
      />
      <TextField
        label="Article Button Text"
        value={storySummaryButtonText as string}
        onChange={(value: string) => update('storySummaryButtonText', value)}
        errorEnabled={false}
      />
      <TextField
        label="Read Full Story Button"
        value={readFullStoryButton as string}
        onChange={(value: string) => update('readFullStoryButton', value)}
        errorEnabled={false}
      />
      <TextField
        label="Floating Button Text"
        value={floatingButtonText as string}
        onChange={(value: string) => update('floatingButtonText', value)}
        errorEnabled={false}
      />
      <Select
        label="Floating Button Position"
        defaultValue={selectedPositionOption}
        options={FLOATING_BUTTON_POSITION_OPTIONS}
        onChange={(opt: { value: string }) => update('floatingButtonPosition', opt.value)}
      />
      <TextField
        label="You are all caught up"
        value={allCaughtUpText as string}
        onChange={(value: string) => update('allCaughtUpText', value)}
        errorEnabled={false}
      />
      <TextArea
        label="You've read all the stories in Sports Cricket.  We'll notify you when new ones arrive."
        value={allStoriesReadText as string}
        onChange={(value: string) => update('allStoriesReadText', value)}
      />
      <TextField
        label="Time Spent"
        value={timeSpentText as string}
        onChange={(value: string) => update('timeSpentText', value)}
        errorEnabled={false}
      />
      <TextField
        label="Stories Read"
        value={storiesReadText as string}
        onChange={(value: string) => update('storiesReadText', value)}
        errorEnabled={false}
      />
      <p style={{ fontWeight: 'bold', margin: 0 }}>Custom Code</p>
      <Callout
        variant="warning"
        title="Warning"
        content="The code may contain malicious script or may negatively affect the performance. Be cautious before pasting your code."
      />
      <TextArea
        label="Widget"
        value={checkValidEncodedCustomCode(widgetCode)}
        onChange={(value: string) => update('widgetCode', encodeCustomCode(value))}
        hint="Paste valid HTML code here. This will be inserted after every 4 shorts. Widgets must not exceed 300px in width and 600px in height."
        placeholder="Paste valid HTML code here"
      />
      <TextField
        label="Targeting ID"
        value={targetingId as string}
        placeholder="Enter Targeting ID"
        onChange={(value: string) => update('targetingId', value)}
        errorEnabled={false}
      />
      <TextField
        label="Mobile Placeholder Height (In Pixels)"
        value={mobilePlaceholderHeight as string}
        placeholder="50"
        onChange={(value: string) => update('mobilePlaceholderHeight', value)}
        errorEnabled={false}
      />
      <TextField
        label="Desktop Placeholder Height (In Pixels)"
        value={desktopPlaceholderHeight as string}
        placeholder="50"
        onChange={(value: string) => update('desktopPlaceholderHeight', value)}
        errorEnabled={false}
      />
    </div>
  );
};

export default ShortsSettings;
